import React from 'react';
import { Grid, Typography, Box, Card, CardContent } from '@mui/material';
import { Schedule, AttachMoney, EventAvailable } from '@mui/icons-material';
import PropTypes from 'prop-types';
import moment from 'moment';

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Helper function to format retail times from 24-hour to 12-hour AM/PM
const formatRetailTime = (timeString) => {
  if (!timeString) return '';
  // timeString is in format like "14:00:00" or "14:00"
  return moment(timeString, ['HH:mm:ss', 'HH:mm']).format('h:mm A');
};

export default function AvailableSchedules({ schedules }) {
  if (!schedules || schedules.length === 0) {
    return (
      <Grid container>
        <Grid item xs={12} md={2}>
          <Typography variant="h6" fontWeight="bold">
            Available Time Slots:
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant="body2" color="text.secondary">
            No schedules available at this time.
          </Typography>
        </Grid>
      </Grid>
    );
  }

  // Sort schedules by day of week
  const sortedSchedules = [...schedules].sort((a, b) => {
    const dayA = a.day_of_week?.toLowerCase() || '';
    const dayB = b.day_of_week?.toLowerCase() || '';
    return DAYS_ORDER.indexOf(dayA) - DAYS_ORDER.indexOf(dayB);
  });

  return (
    <Grid container>
      <Grid item xs={12} md={2}>
        <Typography variant="h6" fontWeight="bold">
          Available Time Slots:
        </Typography>
      </Grid>
      <Grid item xs={12} md={10}>
        <Grid container spacing={2}>
          {sortedSchedules.map((schedule) => (
            <Grid item xs={12} sm={6} md={4} key={schedule.id}>
              <Card
                sx={{
                  border: '1px solid #E0E0E0',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(238, 123, 87, 0.2)',
                    transform: 'translateY(-2px)',
                  },
                }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="#EE7B57"
                    sx={{ textTransform: 'capitalize' }}
                    gutterBottom>
                    {schedule.day_of_week}
                  </Typography>

                  {/* Time */}
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Schedule sx={{ fontSize: 18, color: '#666' }} />
                    <Typography variant="body2">
                      {formatRetailTime(schedule.start_time)}
                      {' - '}
                      {formatRetailTime(schedule.end_time)}
                    </Typography>
                  </Box>

                  {/* Price */}
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AttachMoney sx={{ fontSize: 18, color: '#3c99ba' }} />
                    <Typography variant="body2" fontWeight="500">
                      ${schedule.price_per_hour}/hour
                    </Typography>
                  </Box>

                  {/* Available Slots */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <EventAvailable sx={{ fontSize: 18, color: '#EE7B57' }} />
                    <Typography variant="body2" color="text.secondary">
                      {schedule.slots_available} slot{schedule.slots_available !== 1 ? 's' : ''}{' '}
                      available
                    </Typography>
                  </Box>

                  {schedule.slots_available === 0 && (
                    <Box
                      mt={1}
                      sx={{
                        bgcolor: '#FFE0E0',
                        padding: '4px 8px',
                        borderRadius: 1,
                        textAlign: 'center',
                      }}>
                      <Typography variant="caption" color="error" fontWeight="bold">
                        Fully Booked
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
}

AvailableSchedules.propTypes = {
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      day_of_week: PropTypes.string.isRequired,
      start_time: PropTypes.string.isRequired,
      end_time: PropTypes.string.isRequired,
      price_per_hour: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      slots_available: PropTypes.number.isRequired,
    }),
  ),
};

AvailableSchedules.defaultProps = {
  schedules: [],
};
